




































































































































import {Component, Emit, Prop, Watch} from "vue-property-decorator";
import AsiDialogForm from "../common/AsiDialogForm.vue";
import AsiDialogFormLayout from "../common/AsiDialogFormLayout.vue";
import Report from "../../models/report/Report";
import {AxiosError} from "axios";
import ReportLinesUpdate from "@/models/reportLine/ReportLinesUpdate";
import AsiTabs from "@/components/common/AsiTabs";
import AsiContentContainer from "@/components/common/AsiContentContainer.vue";
import Order from "@/models/order/Order";
import Company from "@/models/company/Company";
import {SnackbarMessage} from "@/models/SnackbarMessage";
import AsiBtn from "@/components/common/AsiBtn.vue";
import AsiTextField from "@/components/common/AsiTextField";
import {formatCurrency, roundToFiveCents} from "@/helpers/functions_asi";
import ReportLineUpdate from "@/models/reportLine/ReportLineUpdate";
import AsiCard from "@/components/common/AsiCard.vue";
import ReportSimple from "@/models/report/ReportSimple";
import i18n from "@/i18n";

class ButtonOption {
	public icon!: string;
	public entity!: string;
	public attribute!: string;
	public hourlyRate!: number | null;
	public dailyLumpSum!: number | null;
	public amount!: number | null;

	constructor(icon: string, entity: string, attribute: string, hourlyRate: number | null, dailyLumpSum: number | null, amount: number | null) {
		this.icon = icon;
		this.entity = entity;
		this.attribute = attribute;
		this.hourlyRate = hourlyRate;
		this.dailyLumpSum = dailyLumpSum;
		this.amount = amount;
	}

	public get label(): string {
		let rate: string | null = null;
		if (this.hourlyRate !== null) {
			rate = formatCurrency(this.hourlyRate, 'CHF');
		} else if (this.dailyLumpSum !== null) {
			rate = formatCurrency(this.dailyLumpSum, 'CHF');
		}

		return rate === null ? i18n.t('uiTerms.noRate').toString() : rate;
	}
}

@Component({
	components: {AsiCard, AsiTextField, AsiBtn, AsiContentContainer, AsiTabs, AsiDialogFormLayout}
})
export default class ReportUpdateDialogReportLines extends AsiDialogForm {

	@Prop({type: Object, required: true})
	public report!: Report;

	private model: ReportLinesUpdate | null = null;
	private loadingCompany: boolean = false;
	private loadingOrder: boolean = false;

	private activeTab = 0;
	private company: Company | null = null;
	private order: Order | null = null;
	private buttonOptions: ButtonOption[] = [];

	private get drivingJobLine(): ReportLineUpdate | null {
		if (this.model === null) return null;
		return this.model.updatedReportLines.find(rl => rl.id = this.report.drivingJobLineId) ?? null;
	}

	private get updatableLines(): ReportLineUpdate[] {
		if (this.model === null) return [];
		return this.model.updatedReportLines.filter(rl => rl.id !== this.report.drivingJobLineId);
	}

	private get amountTotal(): number {
		if (this.model === null) return 0;

		return this.model?.updatedReportLines.reduce((sum, curLine) => {
			return sum + (curLine.amountValue ?? 0);
		}, 0);
	}

	private get amountTotalFormatted(): string {
		return formatCurrency(this.amountTotal, 'CHF') ?? '';
	}

	private get missionHourLines(): string[] | null {
		if (this.report === null) return null;
		return ReportSimple.missionHourLines(this.report);
	}

	private get breakDuration(): string | null {
		if (this.report === null) return null;
		return ReportSimple.breakDuration(this.report);
	}

	private get workingHours(): string | null {
		if (this.report === null) return null;
		return ReportSimple.workingHours(this.report);
	}

	public created(): void {
		this.setReportInternal();
		this.loadCompany();
		this.loadOrder();
	}

	@Emit('save')
	public save(event: Event): Event {
		return event;
	}

	@Emit('cancel')
	public cancel(event: Event): Event {
		this.setReportInternal();
		const form = this.$refs.form as HTMLFormElement;
		this.reset([], form);
		this.activeTab = 0;
		return event;
	}

	private addLine(): void {
		if (this.model === null) return;
		this.model.updatedReportLines.push(new ReportLineUpdate());
	}

	private deleteLine(line: ReportLineUpdate): void {
		if (this.model === null || line.id === this.report.drivingJobLineId) return;
		this.model.updatedReportLines = this.model.updatedReportLines.filter(l => l !== line);
	}

	@Watch('report', {deep: true})
	private onReportChanged(): void {
		this.setReportInternal();
		this.loadCompany();
		this.loadOrder();
	}

	private setReportInternal(): void {
		if (this.model === null) {
			this.model = new ReportLinesUpdate(this.report);
		} else {
			this.model.setDetailModel(this.report);
		}
	}

	private createButtonOptions(): void {
		const workingMinutes = ReportSimple.calculateWorkingDurationMinutes(this.report);

		this.buttonOptions = [
			new ButtonOption(
				this.icons.company,
				this.$t('company.singular').toString(),
				this.$t('company.hourlyRate').toString(),
				this.company?.hourlyRate ?? null,
				null,
				this.company === null || this.company.hourlyRate === null ? null : roundToFiveCents((workingMinutes / 60) * this.company.hourlyRate)
			),
			new ButtonOption(
				this.icons.company,
				this.$t('company.singular').toString(),
				this.$t('company.dailyLumpSum').toString(),
				null,
				this.company?.dailyLumpSum ?? null,
				this.company?.dailyLumpSum ?? null
			),
			new ButtonOption(
				this.icons.order,
				this.$t('order.singular').toString(),
				this.$t('order.hourlyRate').toString(),
				this.order?.hourlyRate ?? null,
				null,
				this.order === null || this.order.hourlyRate === null ? null : roundToFiveCents((workingMinutes / 60) * this.order.hourlyRate)
			),
			new ButtonOption(
				this.icons.order,
				this.$t('order.singular').toString(),
				this.$t('order.dailyLumpSum').toString(),
				null,
				this.order?.dailyLumpSum ?? null,
				this.order?.dailyLumpSum ?? null
			),
		];
	}

	private applyOption(option: ButtonOption): void {
		if (this.drivingJobLine === null || option.amount === null) return;

		this.drivingJobLine.hourlyRateValue = option.hourlyRate;
		this.drivingJobLine.dailyLumpSumValue = option.dailyLumpSum;
		this.drivingJobLine.amountValue = option.amount;
	}

	private requestSave(event: Event): void {
		if (this.model === null) return;

		this.loading = true;
		this.$reportService.updateReportLines(this.report.id, this.model)
			.then(() => this.save(event))
			.catch((err: AxiosError) => {
				this.errors = err.response?.data;
			})
			.finally(() => {
				this.loading = false;
			});
	}

	private loadCompany(): void {
		if (this.model === null) return;

		this.loadingCompany = true;
		this.$companyService.company(this.report.order.company.id)
			.then(company => {
				this.company = company;
				this.createButtonOptions();
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loadingCompany = false;
			});
	}

	private loadOrder(): void {
		if (this.model === null) return;

		this.loadingOrder = true;
		this.$orderService.order(this.report.order.id)
			.then(order => {
				this.order = order;
				this.createButtonOptions();
			})
			.catch(() => {
				this.$store.commit('ui/showSnackbar', {
					message: this.$t('snackbar.loadingError'),
					color: 'error',
				} as SnackbarMessage);
			})
			.finally(() => {
				this.loadingOrder = false;
			});
	}

}
